import { createEffect, createMemo, createSignal } from "solid-js";
import { isServer } from "solid-js/web";

import { postEnterGame } from "@/api/clientApi/game.ts";
import { DialogType } from "@/components/Dialogs/types.ts";
import { useDialog } from "@/components/Dialogs/useDialog.ts";
import { CURRENCY_NAME, LocalStorageKey } from "@/constants/constants";
import { ErrorNumberEnum } from "@/enum/request.ts";
import { UseEnterGameProps } from "@/hooks/game/useEnterGame.ts";
import { getUriTabName } from "@/hooks/useCasino";
import { getLangKey, getLocale, t } from "@/i18n";
import { LocaleMap } from "@/i18n/config";
import { urlAddParam } from "@/lib/helper";
import { getLocalizedPath, getRealPath } from "@/lib/language";
import { getLocalStorageItem } from "@/lib/localStorage.ts";
import { isValidNotEmpty } from "@/lib/validateUtils.ts";
import { checkModalPath } from "@/router/modalRoutes";
import { deviceInfo } from "@/stores/deviceStore.ts";
import { setCurGameId } from "@/stores/gameStore.ts";
import { KYCStore } from "@/stores/kycStore.ts";
import { routerStore, setRouterStore } from "@/stores/routerStore";
import { sysConfigStore } from "@/stores/sysConfigStore.ts";
import { getUserPoint, userStore } from "@/stores/userStore.ts";
import { setWebSocketStore } from "@/stores/websocketStore.ts";
import { UserKycAuthActionType } from "@/types/compontent/base/baseEnum.ts";
import { EnterGameReq } from "@/types/swagger/extGame.ts";
import { useLocation, useNavigate } from "@solidjs/router";

import { useUrlStorage } from "./useUrlStorage";

// 定义 location state 的类型
interface LocationState {
  fromInternal?: boolean;
}

/**
 * 延迟设置模态框路由;
 *
 * 需要异步设置, 不然路由还未跳转, currentRoute就更新了新值, 导致了/src/router/index.tsx中
 * createEffect(on(createMemo(() => routerStore.currentRoute)))先一步执行,
 * 从而在该函数中currentPath获取到还未跳转的url地址; 界面中会表现出:
 * https://zendao.baidu.date/bug-view-4190.html
 * https://zendao.baidu.date/bug-view-4181.html 类似的路由已经跳转但是模态框不能正常关闭的错误
 *
 * @param targetCurrentRoute 需要被设置的RouterStore中currentRoute的值
 */
export function delaySetCurrentRoute(targetCurrentRoute: null | string) {
  setTimeout(() => {
    setRouterStore("currentRoute", targetCurrentRoute);
  }, 6);
}

// 自定义 Router 钩子
export function useRouter() {
  const location = useLocation<LocationState>();
  const navigate = useNavigate();
  const { showDialog, operateDialog } = useDialog();
  const [pathname, setPathname] = createSignal(
    typeof window !== "undefined"
      ? window.location.pathname
      : location.pathname,
  );

  // 创建响应式的 search 参数
  const [search, setSearch] = createSignal(
    typeof window !== "undefined" ? window.location.search : location.search,
  );

  // 监听 search 变化
  createEffect(() => {
    if (typeof window !== "undefined") {
      setSearch(window.location.search);
    }
  });

  const params = createMemo(() => {
    return new URLSearchParams(search());
  });

  // 获取当前语言
  const getCurrentLang = () => {
    const path = location.pathname;
    const parts = path.split("/").filter(Boolean);
    return parts[0] || getLocale();
  };

  // 获取查询参数
  const getParam = (key: string) => {
    return params().get(key);
  };

  // 获取所有查询参数
  const getAllParams = () => {
    const result: Record<string, string> = {};
    params().forEach((value, key) => {
      result[key] = value;
    });
    return result;
  };

  // 检查是否存在某个查询参数
  const hasParam = (key: string) => {
    return params().has(key);
  };

  // 更新单个查询参数
  const setParam = (key: string, value: string | number | boolean) => {
    if (typeof window === "undefined") return;
    const newQuery = new URLSearchParams(params());
    newQuery.set(key, String(value));
    const newUrl = `${window.location.pathname}?${newQuery.toString()}`;
    window.history.pushState({}, "", newUrl);
    console.log(`setParam path: ${newUrl}`);
    setPathname(newUrl);
    push(newUrl);
  };

  // 更新多个查询参数
  const updateQuery = (
    queryParams: Record<string, string | number | boolean>,
  ) => {
    const newQuery = new URLSearchParams(params());
    Object.entries(queryParams).forEach(([key, value]) => {
      newQuery.set(key, String(value));
    });
    const newUrl = `${window.location.pathname}?${newQuery.toString()}`;
    window.history.pushState({}, "", newUrl);
    console.log(`updateQuery path: ${newUrl}`);
    setPathname(newUrl);
  };

  // 删除查询参数
  const removeParam = (key: string) => {
    if (typeof window === "undefined") return;
    const newQuery = new URLSearchParams(params());
    newQuery.delete(key);
    const queryString = newQuery.toString();
    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;
    window.history.pushState({}, "", newUrl);
    console.log(`removeParam path: ${newUrl}`);
    setPathname(newUrl);
  };

  // 清除所有查询参数
  const clearParams = () => {
    if (typeof window === "undefined") return;
    const newUrl = pathname();
    window.history.pushState({}, "", newUrl);
    console.log(`clearParams path: ${newUrl}`);
    setPathname(newUrl);
  };
  /**
   * 跳转到游戏分类页面
   *
   * @param gclass 游戏分类ID 可以传gclass 或者 top hot new
   * @param click_type 游戏分类类型 3厂商tab
   */
  const toCasino = (gclass: number | string, click_type?: number) => {
    let path = "/casino";
    if (gclass) {
      path += `?tab=${getUriTabName(Number(gclass), click_type)}`;
    }
    push(path);
  };

  // 导航到新路径
  const push = (path: string) => {
    console.log(`push path: ${path}`);
    const cleanPath = path.split(/[?#]/)[0];
    const langpath = getLocalizedPath(path);

    console.log(`push path: ${path} langpath: ${langpath}`);
    if (checkModalPath(path)) {
      console.log(`push checkModalPath path: ${langpath}`);
      if (!handleLoginBlackList(path)) return;
      window.history.pushState({}, "", langpath);
      setPathname(langpath);
      delaySetCurrentRoute(cleanPath);
    } else {
      console.log(`push navigate path: ${langpath}`);
      setRouterStore("history", (prev) => [...prev, cleanPath]);
      if (location.pathname === langpath) {
        const currentPath = getRealPath(window.location.pathname);
        if (checkModalPath(currentPath) && routerStore.currentRoute !== null) {
          delaySetCurrentRoute(null);
          window.history.back();
          return;
        }
      }
      delaySetCurrentRoute(null);
      navigate(langpath || "/");
    }
  };

  // 替换当前路径
  const replace = (path: string) => {
    const langpath = getLocalizedPath(path);
    if (checkModalPath(path)) {
      if (!handleLoginBlackList(path)) return;
      window.history.replaceState({}, "", langpath);
      setPathname(langpath);
      delaySetCurrentRoute(path);
    } else {
      navigate(langpath, { replace: true });
      delaySetCurrentRoute(null);
    }
  };

  // 返回上一页
  const back = () => {
    if (typeof window === "undefined") return;
    const referrer = document.referrer;

    // 检查 referrer 是否来自外部站点
    let isReferrerFromExternal = false;
    if (referrer) {
      try {
        const referrerUrl = new URL(referrer);
        const currentOrigin = window.location.origin;
        // 如果 referrer 的 origin 与当前站点不同，说明来自外部站点
        isReferrerFromExternal = referrerUrl.origin !== currentOrigin;
      } catch {
        // 如果 referrer 不是有效的 URL，也视为外部来源
        isReferrerFromExternal = true;
      }
    }

    // 如果来自外部站点，或者 referrer 为空（可能是直接输入URL访问），直接跳转到主页
    // 只有当 referrer 存在且来自内部站点时，才允许执行 navigate(-1)
    if (isReferrerFromExternal) {
      push("/");
      return;
    }

    // 确保 referrer 来自内部站点，且满足其他条件时才执行返回
    if (
      (referrer && referrer.includes(location.pathname)) ||
      window.history.length > 2
    ) {
      if (checkModalPath(getRealPath(window.location.pathname))) {
        // setRouterStore("currentRoute", null);
        delaySetCurrentRoute(null);
      }
      const cleanPath = getFirstPathSegment(window.location.pathname);
      if (
        !userStore.isLogin &&
        routerStore.loginBlackList.includes(cleanPath)
      ) {
        push("/");
      } else navigate(-1);
    } else {
      push("/");
    }
  };

  // 前进
  const forward = () => {
    if (typeof window === "undefined") return;
    window.history.forward();
  };
  // 获取路径的第一个段落
  const getFirstPathSegment = (path: string) => {
    const pathParts = path.split("/").filter(Boolean);
    const firstPath = pathParts[0];
    const secondPath = pathParts[1];
    const isLanguagePath = Object.keys(LocaleMap).includes(firstPath);
    return isLanguagePath ? secondPath : firstPath;
  };

  const handleNoLogin = () => {
    const lastLoginTime = getLocalStorageItem(LocalStorageKey.LastLoginTime, 0);
    // 判断是否登录过
    if (lastLoginTime) {
      push("/login");
    } else {
      push("/register");
    }
  };

  const getEnterGameResponsibleGaming = () => {
    const { getStoredParams } = useUrlStorage();
    const urlParams = getStoredParams().params;
    const ic = urlParams?.ic || "";
    const rm = urlParams?.rm || "";
    const ch = urlParams?.ch || "";
    let url = window.location.href;
    if (ic) {
      url = urlAddParam(url, `ic=${ic}`);
    }
    if (rm) {
      url = urlAddParam(url, `rm=${rm}`);
    }
    if (ch) {
      url = urlAddParam(url, `ch=${ch}`);
    }
    return url;
  };

  // 进入游戏预检拦截功能
  const preCheckEnterGame = async (checkParams: UseEnterGameProps) => {
    const sportId = sysConfigStore.sport_config.sport_game_id;
    if (Number(checkParams.gid) !== Number(sportId) && !userStore.isLogin) {
      // 不是体育，并且没有登录
      handleNoLogin();
      return;
    } else if (
      Number(checkParams.gid) === Number(sportId) &&
      !userStore.isLogin
    ) {
      // 是体育，并且没有登录。放过
      return true;
    }
    const kycRes = await KYCStore.getUserKycAuthByAction(
      UserKycAuthActionType.bet,
    );
    if (kycRes) {
      // 检查kyc(BR), 检查游戏进行中, 争霸赛
      // const indexUrl = SERVER_URL; //window.location.host;
      const indexUrl = window.location.host;
      const currentGid = Number(checkParams.gid);
      const point = getUserPoint(userStore.pointType) || 0;

      const params: EnterGameReq = {
        gid: currentGid,
        demo: 0,
        genNo: 0,
        point,
        action_id: "0",
        race: "",
        lang: getLangKey(),
        os_type: deviceInfo.os_type || 0,
        client: deviceInfo.mobile ? "h5" : "web",
        point_type:
          userStore.currentGamePointType || userStore.walletPointType || 0,
        currency: CURRENCY_NAME,
        externalURL: `https://${indexUrl}`,
        homeUrl: `https://${indexUrl}/res/gameclose.html`,
        cashier: `https://${indexUrl}/res/gameclose.html?pop=deposit`,
        responsibleGaming: getEnterGameResponsibleGaming(),
        item_id: isValidNotEmpty(checkParams?.itemId) ? checkParams.itemId : "",
        free_bet_point_type: undefined,
      };

      const res = await postEnterGame(params, userStore.isLogin)?.catch(
        (error) => {
          const errorNo = Number(error._errno);
          if (errorNo == 1606) {
            // 暂无
          } else if (errorNo == ErrorNumberEnum.EnterGameLock) {
            if (error._param && error._param[0] && error._param[0]._gid) {
              const _gid = error._param[0]._gid;
              showDialog({
                type: DialogType.Tips,
                data: {
                  content: t("tips.game_content"),
                  title: t("tips.tips"),
                  cancelText: t("tips.cancel"),
                  confirmText: t("tips.play"),
                  onConfirm: () => {
                    push(`/game/${_gid}`);
                  },
                  onCancel: () => {
                    // back();
                  },
                },
              });
            }
          }
          // 争霸赛相关
          // } else if () {
          //
          // }
          return false;
        },
      );
      if (res) {
        setWebSocketStore({
          curGid: currentGid,
          curGUrl: res?.url,
          curGBody: res?.body,
        });
        setCurGameId(currentGid, res?.url, res?.body, res?.betby_rsp, res?.ext);
        // 可以用返回值写逻辑, 也可以放进callback执行
        checkParams?.callback?.();
        return res;
      } else return false;
    } else return false;
  };

  const toSport = (sportId: number | string, itemId?: number | string) => {
    if (!sportId) return;
    let path = "/sports";
    path += `/${sportId}`;
    push(path);
  };

  const toDeposit = (params?: {
    replace?: boolean;
    dialogCallback?: unknown;
    itemId?: string;
  }) => {
    if (!userStore.isLogin) {
      handleNoLogin();
      return;
    }
    KYCStore.getUserKycAuthByAction(UserKycAuthActionType.deposit).then(
      (res) => {
        if (res) {
          if (params?.dialogCallback) {
            operateDialog("Deposit", true, {
              closeCallback: () => {
                params?.dialogCallback?.();
              },
              ...(params?.itemId && {
                itemId: params?.itemId,
              }),
            });
          } else {
            if (params?.replace) {
              replace(
                `/deposit${params?.itemId ?? `?itemId=${params?.itemId}`}`,
              );
            } else {
              push(`/deposit${params?.itemId ?? `?itemId=${params?.itemId}`}`);
            }
          }
        }
      },
    );
  };

  const toWithdraw = (params?: {
    replace?: boolean;
    dialogCallback?: unknown;
    url?: string;
  }) => {
    if (!userStore.isLogin) {
      handleNoLogin();
      return;
    }
    KYCStore.getUserKycAuthByAction(UserKycAuthActionType.withdraw).then(
      (res) => {
        if (res) {
          if (params?.dialogCallback) {
            operateDialog("Withdraw", true, {
              closeCallback: () => {
                params?.dialogCallback?.();
              },
            });
          } else {
            if (params?.replace) replace(`/withdraw${params?.url || ""}`);
            else push(`/withdraw${params?.url || ""}`);
          }
        }
      },
    );
  };

  // 处理登录黑名单
  const handleLoginBlackList = (path?: string) => {
    const cleanPath = path
      ? path.replace(/^\/|\/$/g, "") || ""
      : routerStore.currentRoute || "";
    if (routerStore.loginBlackList.includes(cleanPath) && userStore.isLogin) {
      return false;
    }
    return true;
  };

  return {
    location,
    pathname,
    params,
    getParam,
    getAllParams,
    hasParam,
    setParam,
    updateQuery,
    removeParam,
    clearParams,
    push,
    back,
    forward,
    search: location.search,
    getCurrentLang,
    replace,
    getFirstPathSegment,
    toCasino,
    preCheckEnterGame,
    toSport,
    toDeposit,
    toWithdraw,
    handleLoginBlackList,
    handleNoLogin,
  };
}
